import { ColumnDef, SortingState } from "@tanstack/react-table";
import DOMPurify from "dompurify";

import { ServicePagination } from "~/api";
import { AdminBanner } from "~/api/banner.types";
import { Button } from "~/shared";
import { icons } from "~/ui";
import { Chip, Toggle } from "~/ui/common";
import { PaginatedTable } from "~/ui/common/PaginatedTable";

interface BannerTableProps {
  banners: AdminBanner[] | undefined;
  pagination: ServicePagination | undefined;
  isLoading: boolean;
  canManage: boolean;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
  setCurrentPage: (p: number) => void;
  onRowClick: (banner: AdminBanner) => void;
  onDelete: (banner: AdminBanner) => void;
  onToggle: (banner: AdminBanner) => void;
}

export const BannerTable = ({
  banners,
  pagination,
  isLoading,
  canManage,
  sorting,
  setSorting,
  setCurrentPage,
  onRowClick,
  onDelete,
  onToggle,
}: BannerTableProps) => {
  const baseColumns: ColumnDef<AdminBanner>[] = [
    {
      id: "content",
      header: "Content",
      accessorKey: "content",
      enableSorting: false,
      meta: { cellClassName: "w-full max-w-0" },
      cell: ({ row }) => (
        <div
          className="truncate text-gray-700 [&_*]:inline"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(row.original.content),
          }}
        />
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "is_active",
      enableSorting: false,
      cell: ({ row }) => (
        <Chip
          type={row.original.is_active ? "success" : "neutral"}
          className="text-base"
        >
          {row.original.is_active ? "Active" : "Inactive"}
        </Chip>
      ),
    },
    {
      id: "created_at",
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    },
  ];

  const actionsColumn: ColumnDef<AdminBanner> = {
    id: "actions",
    header: "Actions",
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
          <Toggle
            checked={row.original.is_active}
            onChange={() => onToggle(row.original)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row.original);
          }}
        >
          <icons.outline.TrashIcon className="h-4 w-4" />
        </Button>
      </div>
    ),
  };

  const columns = canManage ? [...baseColumns, actionsColumn] : baseColumns;

  return (
    <PaginatedTable
      data={banners}
      pagination={pagination}
      columns={columns}
      isLoadingData={isLoading}
      sorting={sorting}
      setSorting={setSorting}
      setCurrentPage={setCurrentPage}
      onRowClick={onRowClick}
    />
  );
};
